package com.eladfinish.bible.contest.screens.mikud.ui

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.mikud_loading
import com.eladfinish.bible.contest.mikud_save
import com.eladfinish.bible.contest.navigation.MikudScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.back.InterceptBackWhen
import com.eladfinish.bible.contest.navigation.screenKey
import com.eladfinish.bible.contest.screens.mikud.MikudStore
import com.eladfinish.bible.contest.screens.mikud.model.BookClickData
import com.eladfinish.bible.contest.screens.mikud.model.BookState
import com.eladfinish.bible.contest.screens.mikud.model.ChapterClickData
import com.eladfinish.bible.contest.screens.mikud.mvi.MikudAction
import com.eladfinish.bible.contest.screens.mikud.mvi.MikudEffect
import com.eladfinish.bible.contest.screens.mikud.ui.dialogs.MikudDialogs
import com.eladfinish.bible.contest.screens.mikud.usecases.BuildBooksListUseCase
import com.eladfinish.bible.contest.ui.common.ActionButton
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.common.SetScreenIsLoading
import com.eladfinish.bible.contest.ui.menus.actions.MikudToolbarAction
import com.eladfinish.bible.contest.ui.menus.viewModels.ToolbarActionBusViewModel
import com.eladfinish.bible.contest.utils.HebrewWithBackgroundPreview
import dev.icerock.moko.resources.StringResource
import kotlinx.collections.immutable.ImmutableList
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun MikudScreen(
    store: MikudStore = koinViewModel(),
    showToast: (String) -> Unit,
    showToastStringResource: (StringResource) -> Unit,
    toolbarActionBus: ToolbarActionBusViewModel,
    onNavigateBack: () -> Unit,
) {
    val state by store.observeState().collectAsState()

    val currentShowToast by rememberUpdatedState(showToast)
    val currentShowToastStringResource by rememberUpdatedState(showToastStringResource)
    val currentOnNavigateBack by rememberUpdatedState(onNavigateBack)

    InterceptBackWhen(
        enabled = true, // state.dialogState != MikudState.DialogState.None || state.hasUnsavedChanges
    ) {
        store.dispatch(MikudAction.BackClicked)
    }

    MikudToolbarBindings(
        onAction = store::dispatch,
        toolbarActionBus = toolbarActionBus,
    )

    LaunchedEffect(Unit) {
        store.observeSideEffect().collect { effect ->
            when (effect) {
                is MikudEffect.ShowToast -> {
                    currentShowToast(effect.message)
                }

                is MikudEffect.ShowToastRes -> {
                    currentShowToastStringResource(effect.resource)
                }

                MikudEffect.NavigateBack -> currentOnNavigateBack()
            }
        }
    }

    MikudScreenContent(
        onSaveMikud = {
            store.dispatch(MikudAction.SaveMikudClicked)
        },
        onLoadMikud = {
            store.dispatch(MikudAction.LoadMikudClicked)
        },
        onBookClick = {
            store.dispatch(MikudAction.BookToggled(bookClickData = it))
        },
        onChapterClick = {
            store.dispatch(MikudAction.ChapterToggled(chapterClickData = it))
        },
        isDebug = state.isDebugMode,
        mikudState = state.mikudState,
        selectedBook = state.selectedBookIndex,
        hebrewGender = state.hebrewGender
    )

    SetScreenIsLoading(state.isLoading)

    MikudDialogs(
        dialogState = state.dialogState,
        onAction = store::dispatch,
    )
}

@Composable
private fun MikudToolbarBindings(
    onAction: (MikudAction) -> Unit,
    toolbarActionBus: ToolbarActionBusViewModel,
) {
    val screenKey = MikudScreenNavigationDestination::class.screenKey

    val onDispatch by rememberUpdatedState<(MikudToolbarAction) -> Unit> { action ->
        onAction(MikudAction.ToolbarClicked(toolbarAction = action))
    }

    LaunchedEffect(screenKey) {
        toolbarActionBus.setActiveScreenKey(screenKey)
        toolbarActionBus.events.collect { event ->
            if (event.screenKey != screenKey) return@collect
            val action = event.action as? MikudToolbarAction ?: return@collect
            onDispatch(action)
        }
    }
}

@Composable
private fun MikudScreenContent(
    modifier: Modifier = Modifier,
    onSaveMikud: () -> Unit,
    onLoadMikud: () -> Unit,
    onBookClick: (BookClickData) -> Unit,
    onChapterClick: (ChapterClickData) -> Unit,
    isDebug: Boolean,
    mikudState: ImmutableList<BookState>,
    selectedBook: Int?,
    hebrewGender: HebrewGender,
) {
    Column {
        Row(
            modifier = Modifier.fillMaxWidth()
        ) {
            MikudActionButton(
                stringResource = SharedRes.strings.mikud_loading,
                onClick = onLoadMikud,
                hebrewGender = hebrewGender,
                isDebug = isDebug,
            )

            MikudActionButton(
                stringResource = SharedRes.strings.mikud_save,
                onClick = onSaveMikud,
                hebrewGender = hebrewGender,
                isDebug = isDebug,
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(
                modifier = Modifier.weight(5.5f),
                horizontalAlignment = Alignment.Start,
            ) {
                BookList(
                    modifier = modifier,
                    mikudState = mikudState,
                    onClick = {
                        onBookClick.invoke(it)
                    }
                )
            }
            Column(
                modifier = Modifier.weight(4.5f),
                horizontalAlignment = Alignment.Start,
            ) {
                selectedBook?.let {
                    ChapterList(
                        mikudState = mikudState,
                        bookIndex = it,
                        onClick = onChapterClick,
                    )
                }
            }
        }
    }
}

@Composable
private fun RowScope.MikudActionButton(
    stringResource: StringResource,
    onClick: () -> Unit,
    hebrewGender: HebrewGender,
    isDebug: Boolean,
) {
    ActionButton(
        stringResource = stringResource,
        onClick = onClick,
        hebrewGender = hebrewGender,
        fontSize = 24.sp,
        isDebug = isDebug,
    )
}

@HebrewWithBackgroundPreview
@Composable
private fun MikudScreenPreview(
    onBookClick: (BookClickData) -> Unit = {},
    onChapterClick: (ChapterClickData) -> Unit = {},
) {
    PreviewTheme {
        MikudScreenContent(
            onSaveMikud = {},
            onLoadMikud = {},
            onBookClick = onBookClick,
            onChapterClick = onChapterClick,
            isDebug = false,
            mikudState = BuildBooksListUseCase().invoke(full = true),
            selectedBook = 0,
            hebrewGender = HebrewGender.MALE,
        )
    }
}
